import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { resetCrudSelection, useEditingStateWithCtx } from '@/atoms'
import { duplicateSimulation } from '@/features/domain/simulation'
import { changeTerritory, selectTerritoryId } from '@/features/domain/territory'
import { useNotification } from '@/hooks'
import { useNavigate } from '@/routing'
import { useAppDispatch } from '@/store'

import { useDeleteSimulationModal } from '../../../../modals/DeleteSimulationModal'
import { useTexts } from '../useTexts'

export function useActions(simulation: uui.domain.client.rm.SimulationInfo) {
  const { setEditing } = useEditingStateWithCtx('simulation')
  const { show: showDeleteSimulationModal } = useDeleteSimulationModal()
  const dispatch = useAppDispatch()
  const territoryId = useSelector(selectTerritoryId)
  const toast = useNotification()
  const navigate = useNavigate(true)
  const texts = useTexts()

  const onEdit = useCallback(() => {
    setEditing([simulation.id])
  }, [setEditing, simulation])

  const onDelete = useCallback(() => {
    showDeleteSimulationModal()
  }, [showDeleteSimulationModal])

  const onReset = useCallback(() => {
    resetCrudSelection('simulations')
  }, [])

  const onDuplicate = useCallback(async () => {
    try {
      const result = await dispatch(
        duplicateSimulation({
          name: texts.simulationCopyName(simulation.name),
          simulationId: simulation.id,
        }),
      )

      if (!duplicateSimulation.fulfilled.match(result)) {
        throw new Error(result.payload?.message ?? 'Internal error')
      }
    } catch (e) {
      toast.error(e.message)
    }
  }, [dispatch, texts, toast, simulation])

  const onOpenSimulation = useCallback(async () => {
    try {
      const result = await dispatch(
        changeTerritory({
          target: 'simulation',
          simulationId: simulation.id,
          territoryId,
        }),
      )

      if (!changeTerritory.fulfilled.match(result)) {
        throw new Error(result.payload?.message ?? 'Internal error')
      }

      navigate('home')
    } catch (e) {
      toast.error(e.message)
    }
  }, [dispatch, navigate, toast, territoryId])

  return {
    onDelete,
    onEdit,
    onOpenSimulation,
    onReset,
    onDuplicate,
  }
}
